import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {buildFileList} from "./file-utils";
import {UploadAsset} from "./upload-asset";
import {AssetUploadProperty} from "../upload.model";
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {MatFormField, MatLabel, MatOption, MatSelect} from "@angular/material/select";
import {buildUploadPropertiesForm, calculateEligibleFormProperties} from "./upload-form-utils";
import {MatInput} from "@angular/material/input";
import {UploadStateService} from "../upload-state.service";
import {TranslatePipe} from "@ngx-translate/core";
import {MatChipGrid, MatChipInput, MatChipInputEvent, MatChipRemove, MatChipRow} from "@angular/material/chips";
import {MatIcon} from "@angular/material/icon";
import {MatCheckbox, MatCheckboxChange} from "@angular/material/checkbox";
import {AssetProperties, AssetProperty, AssetPropertyValue} from "../../assets/asset.model";
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatProgressBar} from "@angular/material/progress-bar";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {getTitle} from "../upload-utils";
import {MatTooltip} from "@angular/material/tooltip";

@Component({
    selector: 'app-upload-asset',
    imports: [
        ReactiveFormsModule,
        MatSelect,
        MatFormField,
        MatOption,
        MatInput,
        MatLabel,
        TranslatePipe,
        MatChipGrid,
        MatChipRow,
        MatIcon,
        MatChipInput,
        MatChipRemove,
        MatCheckbox,
        MatIconButton,
        MatProgressBar,
        MatProgressSpinner,
        MatButton,
        MatTooltip
    ],
    templateUrl: './upload-asset.component.html',
    styleUrl: './upload-asset.component.scss'
})
export class UploadAssetComponent implements OnInit {

    @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement> | undefined;
    selected: boolean = true;
    hasImage: boolean = true;

    @Input({required: true}) uploadAsset!: UploadAsset;
    @Input({required: true}) editMode: boolean = false;
    @Output() fileChange: EventEmitter<File> = new EventEmitter<File>();
    @Output() propertiesChanged: EventEmitter<AssetProperties> = new EventEmitter<AssetProperties>();
    @Output() selectedChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() remove: EventEmitter<void> = new EventEmitter<void>();
    @Output() validChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    files: FileList | null = null;

    // properties structure from database
    @Input({required: true}) properties!: AssetUploadProperty[];
    // the actual input form
    propertiesForm: FormGroup = new FormGroup({});
    // all properties that can be selected. determines the shape of the form
    eligibleProperties: AssetUploadProperty[] = [];

    constructor(
        private uploadStateService: UploadStateService,
    ) {
    }

    ngOnInit() {
        const fileRef = this.uploadAsset.file;
        if (fileRef) {
            // we could pass the whole drag and drop data transfer object, but we would lose the simple file input param
            this.files = buildFileList(fileRef);
        }
        this.initForm(this.uploadAsset.properties || {});
    }

    ngAfterViewInit() {
        if (!this.editMode) {
            this.fileInput?.nativeElement.click();
        }
    }

    private initForm(assetProperties: AssetProperties) {
        this.eligibleProperties = calculateEligibleFormProperties(this.properties, assetProperties);
        this.propertiesForm = buildUploadPropertiesForm(this.eligibleProperties, assetProperties);
        // currently the form is built anew on every change - this is not optimal but necessary/easier because all valid options are recalculated
        this.propertiesForm.valueChanges.subscribe({
            next: (value) => {
                this.propertiesChanged.emit(value);
                // if the form is updated within this callback it triggers an infinite recursion - therefore we build the form anew
                this.initForm(value);
            }
        });
        // check validity
        this.uploadAsset.valid = this.propertiesForm.valid;
        this.validChange.emit(this.uploadAsset.valid);
    }

    isUploading() {
        return !!this.uploadAsset.file && !this.uploadAsset.fileId;
    }

    fileChanged($event: Event) {
        this.uploadAsset.fileId = undefined;
        let target = $event.target as HTMLInputElement;
        let files = target.files;
        if (files === null) {
            console.warn('no files in event', $event);
            return;
        }
        let theFile = files[0];
        if (theFile === null) {
            console.warn('file at index 0 is null');
        }
        if (!this.editMode) {
            this.propertiesForm.controls['title'].setValue(getTitle(theFile));
        }
        this.fileChange.emit(theFile);
    }

    removeFile() {
        this.remove.emit();
    }

    handleSelectionChange($event: MatCheckboxChange) {
        this.selected = $event.checked;
        this.selectedChange.emit(this.selected);
    }

    handleMasterPropertyChanged(masterProperty: AssetProperty, additive: boolean) {
        let assetUploadProperty = this.eligibleProperties.find(p => p.name === masterProperty.key)
        if (!!assetUploadProperty) {
            let updatedProps = this.computeProperties(
                this.uploadAsset.properties,
                assetUploadProperty,
                masterProperty.value,
                additive
            );
            this.uploadAsset.properties = updatedProps;
            this.propertiesChanged.emit(this.uploadAsset.properties);
            this.initForm(updatedProps);
        }
    }

    private computeProperties(
        currentProperties: AssetProperties,
        assetUploadProperty: AssetUploadProperty,
        newValue: AssetPropertyValue,
        additive: boolean
    ) {
        if (assetUploadProperty.type === 'checkbox') {
            let eligibleValues = assetUploadProperty.values.map(v => v.value);
            if (Array.isArray(newValue)) {
                let values = newValue.filter(v => eligibleValues.includes(v));
                if (assetUploadProperty.isMultiple && additive) {
                    let currentValue = currentProperties[assetUploadProperty.name] || [];
                    if (Array.isArray(currentValue)) {
                        for (let value of values) {
                            if (!currentValue.includes(value)) {
                                currentValue.push(value);
                            }
                        }
                        return {...currentProperties, [assetUploadProperty.name]: currentValue};
                    }
                    return {...currentProperties, [assetUploadProperty.name]: values};
                } else {
                    return {...currentProperties, [assetUploadProperty.name]: values};
                }
            } else if (typeof newValue === 'string' && eligibleValues.includes(newValue)) {
                return {...currentProperties, [assetUploadProperty.name]: newValue};
            } else {
                throw new Error('Invalid value for checkbox property');
            }
        } else {
            if (assetUploadProperty.isMultiple && additive) {
                let currentValue = currentProperties[assetUploadProperty.name] || [];
               // console.log(currentValue, newValue);
                if (Array.isArray(currentValue)) {
                    if (Array.isArray(newValue)) {
                        for (let value of newValue) {
                            if (!currentValue.includes(value)) {
                                currentValue.push(value);
                            }
                        }
                    } else if (typeof newValue === 'string') {
                        if (!currentValue.includes(newValue)) {
                            currentValue.push(newValue);
                        }
                    }
                }
                return {...currentProperties, [assetUploadProperty.name]: currentValue};
            }
            return {...currentProperties, [assetUploadProperty.name]: newValue};
        }
    }

    isPropertyVisible(prop: AssetUploadProperty): boolean {
        return this.uploadStateService.visibleProperties.includes(prop.name);
    }

    getChipEntries(name: string): string[] {
        return this.propertiesForm.get(name)?.value || [];
    }

    removeChipEntry(name: string, entry: string) {
        let entries = this.getChipEntries(name);
        let newEntries = entries.filter(e => e !== entry);
        this.propertiesForm.get(name)?.setValue(newEntries);
    }

    addChipEntry(prop: AssetUploadProperty, $event: MatChipInputEvent) {
        let value = $event.value;
        if (!value) {
            return;
        }
        if (prop.validation?.itemValidation?.maxLength && value.length > prop.validation?.itemValidation?.maxLength) {
            // TODO error?
            return;
        }
        let entries = this.getChipEntries(prop.name);
        let values = value.split(',').map(s => s.trim()).filter(s => s.length > 0);
        entries.push(...values);
        this.propertiesForm.get(prop.name)?.setValue(entries);
        $event.chipInput!.clear();
    }

    handleBlur(name: string, $event: FocusEvent) {
        let target = $event.target as HTMLInputElement;
        let value = target.value;
        if (!value) {
            return;
        }
        let entries = this.getChipEntries(name);
        let values = value.split(',').map(s => s.trim()).filter(s => s.length > 0);
        entries.push(...values);
        this.propertiesForm.get(name)?.setValue(entries);
        target.value = '';
    }
}
